/**
 * engine ⇔ React ⇔ Phaser の接続点(docs/03_architecture.md §4)。
 *
 * 一方向フロー:
 *   window keydown ─(直送)→ TypingSession ─ events →┬→ effects(Phaser演出・+Nポップ・SE)
 *                                                  └→ snapshot(rAFごとに1回だけ store へ)
 *
 * keydown は React を経由せずここから engine を直接叩く(入力遅延50ms要件)。
 * store 更新は rAF で1フレーム1回に間引き、内容が変わらないフレームは書き込まない。
 */

import type {
  PerformanceStage,
  SessionEvent,
  SessionResult,
  TypingSession,
} from '@paltyping/engine';
import { representativeMedia, WordMediaSelector } from '../logic/mediaVariation';
import { assetUrl } from '../services/assets';
import {
  DEFAULT_FIRST_LANGUAGE,
  meaningInFirstLanguage,
  type ContentWord,
  type FirstLanguage,
  type Language,
  type StageBundle,
} from '../services/content';
import type { LearningSink } from '../services/learning';
import { Emitter } from './emitter';
import { keyFromEvent, shouldPreventDefault } from './keys';

/** 画面に出す1問ぶんの情報。 */
export interface PlayWord {
  readonly id: string;
  readonly display: string;
  readonly reading: string;
  readonly ruby: string | null;
  /** 日本語の意味(日本語ドリルの補足 / 英語モードの和訳出題)。 */
  readonly meaning: string | null;
  /** この出題で使う画像(バリエーションから選択済み)。 */
  readonly imageUrl: string | null;
  /** この出題で使う音声(バリエーションから選択済み。出題時・打ち終わり時とも同じ話者)。 */
  readonly audioUrl: string | null;
}

/** HUD・問題表示に必要な状態。rAFごとに最大1回だけ更新される。 */
export interface PlaySnapshot {
  readonly score: number;
  readonly combo: number;
  readonly maxCombo: number;
  readonly gauge: number;
  readonly gaugeMultiplier: number;
  readonly comboBonus: number;
  readonly performanceStage: PerformanceStage;
  readonly questionIndex: number;
  readonly totalQuestions: number | null;
  readonly completedQuestions: number;
  readonly word: PlayWord | null;
  readonly guide: string;
  readonly typed: string;
  readonly remaining: string;
  readonly nextKeys: readonly string[];
  readonly correctKeys: number;
  readonly missKeys: number;
  readonly accuracy: number;
  readonly remainingMs: number | null;
  readonly elapsedMs: number;
  /** 出題からの経過(100ms刻みに丸め済み)。 */
  readonly wordElapsedMs: number;
  /** 無操作時間(100ms刻みに丸め済み)。 */
  readonly idleMs: number;
  /**
   * 現在の語で観測した最長の無操作時間(100ms刻み)。
   * 「いちど手が止まったら助け舟を出したままにする」判定に使う(logic/guide.ts)。
   */
  readonly maxIdleMs: number;
  readonly hasTypedInWord: boolean;
  /** 現在の語でミスタイプしたか。 */
  readonly missedInWord: boolean;
  /** 読みの何文字目を打っているか(ふりがなの下線用)。 */
  readonly unitIndex: number;
}

/** 演出用イベント。 */
export type PlayEffect =
  | { readonly type: 'correct'; readonly gain: number; readonly combo: number }
  | { readonly type: 'miss' }
  /** 新しい単語が画面に出た(英語モードはここで発音音声を鳴らす)。 */
  | { readonly type: 'wordStarted'; readonly word: PlayWord }
  | { readonly type: 'wordCompleted'; readonly word: PlayWord }
  | { readonly type: 'stage'; readonly stage: PerformanceStage }
  | { readonly type: 'fever'; readonly active: boolean };

export interface PlayControllerOptions {
  readonly session: TypingSession;
  readonly bundle: StageBundle;
  readonly language: Language;
  /** 訳を出す言語(ことばの せってい)。既定は ja。 */
  readonly firstLanguage?: FirstLanguage;
  readonly onSnapshot: (snapshot: PlaySnapshot) => void;
  readonly onEnd: (result: SessionResult) => void;
  /**
   * 学習イベントの記録口(docs/07_learning_support.md §2)。
   * 未指定なら何も記録しない。演出・スナップショットには一切影響しない。
   */
  readonly learning?: LearningSink;
  /** テスト差し替え用。 */
  readonly now?: () => number;
  /** 画像・音声バリエーションのシャッフルに使う乱数(既定 Math.random)。 */
  readonly random?: () => number;
  readonly requestFrame?: (callback: (time: number) => void) => number;
  readonly cancelFrame?: (handle: number) => void;
  readonly target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

const IDLE_BUCKET_MS = 100;

function bucket(value: number): number {
  return Math.floor(value / IDLE_BUCKET_MS) * IDLE_BUCKET_MS;
}

/**
 * コンテンツ単語を画面表示用に変換する。アセット未配備なら URL は null。
 *
 * `media` を渡すと画像・音声のバリエーションをシャッフル循環で1つ選ぶ(docs/05 §3c)。
 * 渡さなければ代表アセット(従来挙動)。
 */
export function toPlayWord(
  word: ContentWord,
  language: Language,
  media?: WordMediaSelector,
  firstLanguage: FirstLanguage = DEFAULT_FIRST_LANGUAGE,
): PlayWord {
  // 訳は第一言語(ことばの せってい)のものを出す。無ければ ja へフォールバックする。
  const meaning = meaningInFirstLanguage(word.meaning, firstLanguage);
  const selected = media ? media.select(word, language) : representativeMedia(word, language);
  return {
    id: word.id,
    display: word.display,
    reading: word.reading,
    ruby: word.ruby ?? (word.display === word.reading ? null : word.reading),
    meaning,
    imageUrl: assetUrl(selected.image),
    audioUrl: assetUrl(selected.audio),
  };
}

export class PlayController {
  /** Phaser演出・+Nポップ・SE が購読する。 */
  readonly effects = new Emitter<PlayEffect>();

  private readonly session: TypingSession;
  private readonly options: PlayControllerOptions;
  private readonly now: () => number;
  private readonly requestFrame: (callback: (time: number) => void) => number;
  private readonly cancelFrame: (handle: number) => void;
  private readonly target: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  /** 画像・音声バリエーションの選択器(状態はこのセッション限り)。 */
  private readonly media: WordMediaSelector;

  private frame: number | null = null;
  private unsubscribe: (() => void) | null = null;
  private running = false;
  private signature = '';

  private wordStartedAt = 0;
  private hasTypedInWord = false;
  /** 現在の語でミスタイプしたか(英語ふつうの助け舟の条件)。 */
  private missedInWord = false;
  /** 現在の語で観測した最長の無操作時間(ms)。助け舟を引っ込めないための累積値。 */
  private maxIdleMs = 0;
  private currentWord: PlayWord | null = null;
  /** 打鍵直前の状態(学習イベントの「押すはずだったキー」「かなユニット」の特定用)。 */
  private pendingInput: { readonly expected: string; readonly unitIndex: number } | null = null;

  constructor(options: PlayControllerOptions) {
    this.options = options;
    this.session = options.session;
    this.now = options.now ?? (() => performance.now());
    this.requestFrame = options.requestFrame ?? ((callback) => requestAnimationFrame(callback));
    this.cancelFrame = options.cancelFrame ?? ((handle) => cancelAnimationFrame(handle));
    this.target = options.target ?? window;
    this.media = new WordMediaSelector(options.random);
  }

  /** 開始する。keydown 購読と rAF ループを張る。 */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.unsubscribe = this.session.onEvent(this.handleEvent);
    this.target.addEventListener('keydown', this.handleKeyDown as EventListener, {
      passive: false,
    });
    const at = this.now();
    this.wordStartedAt = at;
    this.session.start(at);
    this.flush(true);
    this.scheduleFrame();
  }

  /** 停止する。未終了なら中断扱いにする。 */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.frame !== null) this.cancelFrame(this.frame);
    this.frame = null;
    this.target.removeEventListener('keydown', this.handleKeyDown as EventListener);
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.effects.clear();
  }

  /** 途中でやめる(リザルトは cleared=false)。 */
  abort(): void {
    this.session.abort(this.now());
  }

  private scheduleFrame(): void {
    this.frame = this.requestFrame(this.tick);
  }

  private readonly tick = (): void => {
    if (!this.running) return;
    this.session.tick(this.now());
    this.flush(false);
    if (this.running) this.scheduleFrame();
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.running) return;
    if (shouldPreventDefault(event)) event.preventDefault();
    const key = keyFromEvent(event);
    if (key === null) return;
    if (this.options.learning) {
      // 受理前の状態を控えておく(ミスの帰属先とかなユニットは打鍵前にしか分からない)。
      const before = this.session.getState();
      this.pendingInput = { expected: before.remaining.charAt(0), unitIndex: before.unitIndex };
    }
    // engine へ直送(React の再レンダリングを待たない)。
    this.session.input(key, this.now());
    this.pendingInput = null;
    this.flush(false);
  };

  private readonly handleEvent = (event: SessionEvent): void => {
    const learning = this.options.learning;
    switch (event.type) {
      case 'sessionStart':
        learning?.start(event.at);
        break;
      case 'wordStart': {
        this.wordStartedAt = event.at;
        this.hasTypedInWord = false;
        this.missedInWord = false;
        this.maxIdleMs = 0;
        const content = this.options.bundle.wordsById.get(event.word.id ?? '');
        // 画像・音声のバリエーションはここで確定させる。以後この出題中は同じものを使う
        // (出題時再生と打ち終わり時再生が同じ話者になる。docs/05 §3c)。
        this.currentWord = content
          ? toPlayWord(content, this.options.language, this.media, this.options.firstLanguage)
          : null;
        learning?.wordStart({
          at: event.at,
          wordId: event.word.id,
          reading: event.word.reading,
          tags: content?.tags,
        });
        if (this.currentWord) {
          this.effects.emit({ type: 'wordStarted', word: this.currentWord });
        }
        break;
      }
      case 'keyAccepted':
        this.hasTypedInWord = true;
        learning?.key({
          at: event.at,
          key: event.key,
          expected: this.pendingInput?.expected ?? event.key,
          accepted: true,
          unitIndex: this.pendingInput?.unitIndex ?? 0,
        });
        this.effects.emit({ type: 'correct', gain: event.gain, combo: event.combo });
        break;
      case 'keyRejected':
        this.missedInWord = true;
        learning?.key({
          at: event.at,
          key: event.key,
          expected: this.pendingInput?.expected ?? event.expected[0] ?? '',
          accepted: false,
          unitIndex: this.pendingInput?.unitIndex ?? 0,
        });
        this.effects.emit({ type: 'miss' });
        break;
      case 'wordCompleted':
        learning?.wordCompleted({ at: event.at, wordId: event.word.id });
        if (this.currentWord) {
          this.effects.emit({ type: 'wordCompleted', word: this.currentWord });
        }
        break;
      case 'stageChanged':
        this.effects.emit({ type: 'stage', stage: event.stage });
        break;
      case 'feverStart':
        this.effects.emit({ type: 'fever', active: true });
        break;
      case 'feverEnd':
        this.effects.emit({ type: 'fever', active: false });
        break;
      case 'sessionEnd':
        this.flush(true);
        // 圧縮集計 → localStorage 保存(+将来のバッチ送信)はここ1か所だけ。
        learning?.end({ at: event.at, result: event.result });
        this.options.onEnd(event.result);
        break;
      default:
        break;
    }
  };

  /** 変化があったときだけ store に流す。 */
  private flush(force: boolean): void {
    const state = this.session.getState();
    const wordElapsed = bucket(Math.max(0, this.now() - this.wordStartedAt));
    const idle = bucket(state.idleMs);
    // 「その語でいちばん長く止まった時間」。助け舟(logic/guide.ts)を出したままにするため。
    this.maxIdleMs = Math.max(this.maxIdleMs, idle);
    const signature = [
      state.score,
      state.combo,
      Math.round(state.gauge),
      state.questionIndex,
      state.completedQuestions,
      state.typed,
      state.remaining,
      state.missKeys,
      this.currentWord?.id ?? '',
      wordElapsed,
      idle,
      state.remainingMs === null ? '' : Math.floor(state.remainingMs / 200),
    ].join('|');
    if (!force && signature === this.signature) return;
    this.signature = signature;

    this.options.onSnapshot({
      score: state.score,
      combo: state.combo,
      maxCombo: state.maxCombo,
      gauge: state.gauge,
      gaugeMultiplier: state.gaugeMultiplier,
      comboBonus: state.comboBonus,
      performanceStage: state.performanceStage,
      questionIndex: state.questionIndex,
      totalQuestions: state.totalQuestions,
      completedQuestions: state.completedQuestions,
      word: this.currentWord,
      guide: state.guide,
      typed: state.typed,
      remaining: state.remaining,
      nextKeys: state.nextKeys,
      correctKeys: state.correctKeys,
      missKeys: state.missKeys,
      accuracy: state.accuracy,
      remainingMs: state.remainingMs,
      elapsedMs: state.elapsedMs,
      wordElapsedMs: wordElapsed,
      idleMs: idle,
      maxIdleMs: this.maxIdleMs,
      hasTypedInWord: this.hasTypedInWord,
      missedInWord: this.missedInWord,
      unitIndex: state.unitIndex,
    });
  }
}
